/**
 * Tier 8–10 creatures: Pterodactyl, T-rex, Dragon (assetId dragon_final), redrawn in code after
 * the original glossy kawaii pictures (assets/merge/v2/scaled/<assetId>.png) so each reads as
 * the same character at a glance: same pose, proportions, colours and small calm face.
 *
 *   Pterodactyl  pink-lilac, wings spread wide, a crest swept up and back with a peach inner
 *                face, a cream beak with a little smile line, round belly, tiny feet and tail.
 *   T-rex        a green bean of a body with a big round head, orange spikes from crown to tail,
 *                cream belly, tiny arms, chunky legs with cream toes, darker green spots.
 *   Dragon       the final tier (also shown large in the victory popup): turquoise, banded gold
 *                horns and spikes, small violet wings, a cream belly plate, gold claws and a
 *                curled tail; its bubble keeps the studded gold band.
 *
 * Style: clean vector shapes, each part outlined in a deeper shade of its own colour, cel lit
 * from the upper left (a light crescent on the lit edge, a shade crescent on the far edge), and
 * glossy white highlight spots. Faces stay small: two dark oval eyes with one white highlight,
 * a tiny curved smile, soft pink blush. No props.
 *
 * Contract (creatures/index.js drawCreature): `{ disc, discPattern?, draw(c, r, pose) }`, drawn
 * centred inside the bubble of radius r (the physics circle), everything within ~0.95 r.
 * `pose.mood` "blink" closes only the eyes; the other kit moods change eyes and mouth only.
 * Every part carries its own outline because `pose.noDisc` bakes the creature alone as the layer
 * that rotates in game over an upright bubble and glare.
 */
import { TAU, hash, sparklePath } from "../toon-kit.js";
import { rgba, smoothPath, bezierPts, tubePath } from "./candy-kit.js";

// ---------------------------------------------------------------------------------------
// Pen, paint and paths (all lengths in units of the radius passed in).

/** Outline beyond each part's edge. */
var PEN = { body: 0.024, part: 0.02, fine: 0.015 };
/** The second outline copy's nudge: the line runs a touch heavier on the shade side. */
var SWELL = [0.005, 0.007];

function tone(ink, shade, base, light) {
  return { ink: ink, shade: shade, base: base, light: light };
}

/** A closed smooth (Catmull-Rom) outline through unit points. */
function blob(pts, r) {
  return function (c) {
    smoothPath(c, pts, r);
  };
}

/**
 * A path from a flat command list in r units: "M" x y, "L" x y, "Q" cx cy x y,
 * "C" x1 y1 x2 y2 x y, "Z". `sx` −1 mirrors it left–right.
 */
function cmds(list, r, sx) {
  var k = sx || 1;
  return function (c) {
    var i = 0;
    while (i < list.length) {
      var op = list[i++];
      if (op === "M") {
        c.moveTo(list[i] * k * r, list[i + 1] * r);
        i += 2;
      } else if (op === "L") {
        c.lineTo(list[i] * k * r, list[i + 1] * r);
        i += 2;
      } else if (op === "Q") {
        c.quadraticCurveTo(list[i] * k * r, list[i + 1] * r, list[i + 2] * k * r, list[i + 3] * r);
        i += 4;
      } else if (op === "C") {
        c.bezierCurveTo(
          list[i] * k * r, list[i + 1] * r,
          list[i + 2] * k * r, list[i + 3] * r,
          list[i + 4] * k * r, list[i + 5] * r
        );
        i += 6;
      } else if (op === "Z") {
        c.closePath();
      }
    }
  };
}

function oval(x, y, rx, ry, rot, r) {
  return function (c) {
    var a = rot || 0;
    c.moveTo((x + Math.cos(a) * rx) * r, (y + Math.sin(a) * rx) * r);
    c.ellipse(x * r, y * r, rx * r, ry * r, a, 0, TAU);
  };
}

/**
 * One cel-shaded part. Layers: the outline in `t.ink` (`pen` beyond the edge, nudged heavier
 * toward the lower right), then clipped to the shape: the light tone, the base everywhere but a
 * crescent along the lit upper-left edge (`lit`), the shade in a crescent along the lower-right
 * edge (`shade`), extra light shapes (`light(c)`), then `inside(c)` details.
 *
 * @param {CanvasRenderingContext2D} c
 * @param {number} r
 * @param {(c: CanvasRenderingContext2D) => void} path
 * @param {{ ink: string, shade: string, base: string, light: string }} t
 * @param {{ pen?: number, lit?: number, shade?: number, light?: Function, inside?: Function, noLine?: boolean }} [o]
 */
function part(c, r, path, t, o) {
  var p = o || {};
  var pen = (p.pen != null ? p.pen : PEN.body) * r;
  var big = r * 4;
  c.save();
  c.lineJoin = "round";
  c.lineCap = "round";
  if (!p.noLine) {
    c.fillStyle = t.ink;
    c.strokeStyle = t.ink;
    c.lineWidth = pen * 2;
    c.beginPath();
    path(c);
    c.fill();
    c.stroke();
    c.save();
    c.translate(SWELL[0] * r, SWELL[1] * r);
    c.beginPath();
    path(c);
    c.fill();
    c.stroke();
    c.restore();
  }
  c.beginPath();
  path(c);
  c.clip();
  var L = (p.lit != null ? p.lit : 0.04) * r;
  var S = (p.shade != null ? p.shade : 0.05) * r;
  c.fillStyle = t.light;
  c.fillRect(-big, -big, big * 2, big * 2);
  c.save();
  c.translate(L * 0.75, L);
  c.beginPath();
  path(c);
  c.restore();
  c.fillStyle = t.base;
  c.fill();
  if (S > 0) {
    c.beginPath();
    c.rect(-big, -big, big * 2, big * 2);
    c.save();
    c.translate(-S * 0.75, -S);
    path(c);
    c.restore();
    c.fillStyle = t.shade;
    c.fill("evenodd");
  }
  if (p.light) {
    c.fillStyle = t.light;
    c.beginPath();
    p.light(c);
    c.fill();
  }
  if (p.inside) p.inside(c);
  c.restore();
}

/** A glossy white highlight spot. */
function gloss(c, r, x, y, rx, ry, rot, a) {
  c.fillStyle = "rgba(255,255,255," + (a != null ? a : 0.85) + ")";
  c.beginPath();
  c.ellipse(x * r, y * r, rx * r, ry * r, rot || 0, 0, TAU);
  c.fill();
}

/** A stroked open line in r units (cmds list), width w (r units). */
function line(c, r, list, color, w, sx) {
  c.save();
  c.lineJoin = "round";
  c.lineCap = "round";
  c.strokeStyle = color;
  c.lineWidth = w * r;
  c.beginPath();
  cmds(list, r, sx)(c);
  c.stroke();
  c.restore();
}

/**
 * A pointed spike / leaf from base centre (bx, by) to tip (tx, ty), half-width w at the base;
 * `bulge` rounds its sides (0 = straight). Unit coords.
 */
function spikePath(bx, by, tx, ty, w, bulge, r) {
  var dx = tx - bx, dy = ty - by, d = Math.hypot(dx, dy) || 1;
  var nx = -dy / d, ny = dx / d;
  var b = bulge != null ? bulge : 0.35;
  var lx = bx + nx * w, ly = by + ny * w, rx = bx - nx * w, ry = by - ny * w;
  var mx = bx + dx * 0.5, my = by + dy * 0.5;
  return cmds([
    "M", lx, ly,
    "Q", mx + nx * w * (1 + b) * 0.85, my + ny * w * (1 + b) * 0.85, tx, ty,
    "Q", mx - nx * w * (1 + b) * 0.85, my - ny * w * (1 + b) * 0.85, rx, ry,
    "Q", bx - dx * 0.15, by - dy * 0.15, lx, ly,
    "Z",
  ], r);
}

// ---------------------------------------------------------------------------------------
// The small kawaii face shared by the three.

/** Kit moods → [eyes, mouth]; null mouth keeps the resting smile (blink). */
var FACE_MOODS = {
  smile: ["open", "smile"],
  grin: ["open", "grin"],
  happy: ["happy", "grin"],
  wow: ["wow", "o"],
  blink: ["closed", null],
  sleepy: ["sleepy", "o"],
  fierce: ["fierce", "grin"],
  cool: ["cool", "smirk"],
  wink: ["wink", "smile"],
  proud: ["happy", "smile"],
};

function faceKinds(pose) {
  var m = FACE_MOODS[pose && pose.mood] || FACE_MOODS.smile;
  return { eyes: m[0], mouth: m[1] || "smile" };
}

var MOUTH_IN = "#7a2438";
var TONGUE = "#ff8fa6";

/**
 * F: eyes [[x, y, rx, ry] left, [..] right]; eye (fill), low (lower-eye glow, optional);
 * hl [[dx, dy, size] in eye radii]; line (closed eyes, brows, mouth); lw (r units);
 * mouth [x0, y0, cx, cy, x1, y1] (the resting smile); blush [[x, y, rx, ry]]; blushCol;
 * skin (lid colour for "cool"); clip(c) keeps blush inside the head; noMouth (the
 * Pterodactyl's beak carries its own).
 */
function kawaiiFace(c, r, F, pose) {
  var k = faceKinds(pose);
  c.save();
  if (F.clip) {
    c.beginPath();
    F.clip(c);
    c.clip();
  }
  for (var b = 0; b < F.blush.length; b++) {
    var B = F.blush[b];
    c.fillStyle = rgba(F.blushCol, 0.38);
    c.beginPath();
    c.ellipse(B[0] * r, B[1] * r, B[2] * r * 1.18, B[3] * r * 1.2, 0, 0, TAU);
    c.fill();
    c.fillStyle = rgba(F.blushCol, 0.62);
    c.beginPath();
    c.ellipse(B[0] * r, B[1] * r, B[2] * r * 0.92, B[3] * r * 0.9, 0, 0, TAU);
    c.fill();
  }
  c.restore();
  if (!F.noMouth) faceMouth(c, r, F, k.mouth);
  for (var s = 0; s < 2; s++) {
    var kind = k.eyes;
    if (kind === "wink") kind = s === 1 ? "happy" : "open";
    faceEye(c, r, F, F.eyes[s], kind, s === 0 ? -1 : 1);
  }
}

function faceEye(c, r, F, E, kind, side) {
  var x = E[0] * r, y = E[1] * r, rx = E[2] * r, ry = E[3] * r;
  c.save();
  c.lineCap = "round";
  c.lineJoin = "round";
  c.strokeStyle = F.line;
  c.lineWidth = F.lw * r;
  if (kind === "closed" || kind === "sleepy") {
    // A gentle closed lid, curving down like a sleeping smile.
    var yy = y + ry * (kind === "sleepy" ? 0.3 : 0.1);
    c.beginPath();
    c.moveTo(x - rx * 1.0, yy - ry * 0.1);
    c.quadraticCurveTo(x, yy + ry * (kind === "sleepy" ? 0.55 : 0.8), x + rx * 1.0, yy - ry * 0.1);
    c.stroke();
  } else if (kind === "happy") {
    c.beginPath();
    c.moveTo(x - rx * 0.95, y + ry * 0.3);
    c.quadraticCurveTo(x, y - ry * 0.95, x + rx * 0.95, y + ry * 0.3);
    c.stroke();
  } else {
    var k = kind === "wow" ? 1.12 : 1;
    rx *= k;
    ry *= k;
    c.fillStyle = F.eye;
    c.beginPath();
    c.ellipse(x, y, rx, ry, 0, 0, TAU);
    c.fill();
    if (F.low) {
      c.save();
      c.clip();
      c.fillStyle = F.low;
      c.beginPath();
      c.ellipse(x, y + ry * 0.62, rx * 0.78, ry * 0.5, 0, 0, TAU);
      c.fill();
      c.restore();
    }
    if (kind === "cool") {
      // A heavy lid over the top of the eye: unbothered.
      c.save();
      c.beginPath();
      c.ellipse(x, y, rx * 1.08, ry * 1.08, 0, 0, TAU);
      c.clip();
      c.fillStyle = F.skin;
      c.fillRect(x - rx * 1.2, y - ry * 1.3, rx * 2.4, ry * 0.95);
      c.restore();
      c.beginPath();
      c.moveTo(x - rx * 1.0, y - ry * 0.36);
      c.lineTo(x + rx * 1.0, y - ry * 0.36);
      c.stroke();
    }
    c.fillStyle = "#ffffff";
    for (var h = 0; h < F.hl.length; h++) {
      var H = F.hl[h];
      if (kind === "cool" && H[1] < 0) continue;
      c.beginPath();
      c.arc(x + H[0] * rx, y + H[1] * ry, H[2] * rx, 0, TAU);
      c.fill();
    }
    if (kind === "fierce") {
      // A short brow dipping toward the nose.
      c.beginPath();
      c.moveTo(x + side * rx * 0.95, y - ry * 1.55);
      c.lineTo(x - side * rx * 0.75, y - ry * 1.2);
      c.stroke();
    }
  }
  c.restore();
}

function faceMouth(c, r, F, kind) {
  var m = F.mouth;
  var x0 = m[0] * r, y0 = m[1] * r, cx = m[2] * r, cy = m[3] * r, x1 = m[4] * r, y1 = m[5] * r;
  var w = (x1 - x0) / 2, mx = (x0 + x1) / 2, my = (y0 + y1) / 2;
  c.save();
  c.lineCap = "round";
  c.lineJoin = "round";
  c.strokeStyle = F.line;
  c.lineWidth = F.lw * r;
  if (kind === "grin") {
    var open = function () {
      c.beginPath();
      c.moveTo(x0, y0);
      c.quadraticCurveTo(mx, my + (cy - my) * 0.35, x1, y1);
      c.bezierCurveTo(x1 - w * 0.1, my + w * 1.25, x0 + w * 0.1, my + w * 1.25, x0, y0);
      c.closePath();
    };
    open();
    c.fillStyle = MOUTH_IN;
    c.fill();
    c.save();
    c.clip();
    c.fillStyle = TONGUE;
    c.beginPath();
    c.ellipse(mx, my + w * 1.0, w * 0.6, w * 0.42, 0, 0, TAU);
    c.fill();
    c.restore();
    open();
    c.stroke();
  } else if (kind === "o") {
    c.beginPath();
    c.ellipse(mx, my + w * 0.3, w * 0.36, w * 0.44, 0, 0, TAU);
    c.fillStyle = MOUTH_IN;
    c.fill();
    c.stroke();
  } else if (kind === "smirk") {
    c.beginPath();
    c.moveTo(x0 + w * 0.15, y0 + w * 0.2);
    c.quadraticCurveTo(cx, cy - w * 0.1, x1 + w * 0.05, y1 - w * 0.25);
    c.stroke();
  } else {
    c.beginPath();
    c.moveTo(x0, y0);
    c.quadraticCurveTo(cx, cy, x1, y1);
    c.stroke();
  }
  c.restore();
}

// ---------------------------------------------------------------------------------------
// Bubble patterns.

/** Star dust: `n` small dots spread over the glass (deterministic). */
function starDust(c, r, seed, n, color, maxA) {
  for (var i = 0; i < n; i++) {
    var a = hash(seed + i * 3.17) * TAU;
    var d = Math.sqrt(hash(seed + i * 7.71)) * 0.88;
    var s = (0.005 + Math.pow(hash(seed + i * 1.37), 2) * 0.018) * r;
    c.fillStyle = rgba(color, (0.3 + 0.7 * hash(seed + i * 5.93)) * (maxA || 0.9));
    c.beginPath();
    c.arc(Math.cos(a) * d * r, Math.sin(a) * d * r, s, 0, TAU);
    c.fill();
  }
}

/** Four-point sparkles [x, y, size] (r units) with a soft halo. */
function starSparkles(c, r, list, halo) {
  for (var i = 0; i < list.length; i++) {
    var s = list[i];
    if (halo) {
      c.fillStyle = rgba(halo, 0.35);
      c.beginPath();
      c.arc(s[0] * r, s[1] * r, s[2] * r * 0.45, 0, TAU);
      c.fill();
    }
    c.fillStyle = "#ffffff";
    c.beginPath();
    sparklePath(c, s[0] * r, s[1] * r, s[2] * r, 0);
    c.fill();
  }
}

/** A soft round glow: `stops` [[t, css colour]] from the centre (x, y) out to radius rad. */
function glow(c, r, x, y, rad, stops) {
  var g = c.createRadialGradient(x * r, y * r, 0, x * r, y * r, rad * r);
  for (var i = 0; i < stops.length; i++) g.addColorStop(stops[i][0], stops[i][1]);
  c.fillStyle = g;
  c.fillRect(-r, -r, r * 2, r * 2);
}

// ---------------------------------------------------------------------------------------
// Tier 8 — Pterodactyl (after pterodactyl.png): pink-lilac, wings spread wide, crest swept up
// to the upper right, a cream beak pointing down-left with a smile line, a round light belly, a
// round haunch with a two-toed foot, a small far foot peeping out, a pointed tail on the right.
//
// Wings, traced from the picture: a bony arm from the shoulder up to a knuckled wrist with a
// little hooked thumb claw, the leading edge arching out to the tip, then two scalloped trailing
// edges meeting at the finger tip. Inside: a light band along the leading edge that runs round
// the wrist into a light finger bone (wrist → scallop point), two deeper pink membrane panels
// either side of the bone, and skin-pink along the arm. The near (right) wing lies over the
// body's flank: its arm line runs into the chest as a tapering crease and its root has no line,
// so the membrane melts into the body. The far (left) wing sits behind the head and body.

var PT_SCALE = 0.96;
var PT_INK = "#85308b";
var PT_SKIN = tone(PT_INK, "#d277d8", "#ee9cee", "#fbc8f8");
var PT_BELLY = tone(PT_INK, "#f1b4ef", "#fbd2f8", "#fff0fd");
/** The wing's membrane panels, a deeper pink than the skin. */
var PT_MEMBRANE = tone(PT_INK, "#d24fd0", "#e867e2", "#f594ef");
/** The light band along the leading edge, round the wrist and down the finger bone. */
var PT_WING_BAND = "#f9b3f2";
var PT_BEAK = tone("#91504a", "#efb76a", "#fbd98c", "#fff2c8");
var PT_CREST = "#f6c38a";
var PT_CREST_LIGHT = "#fde5bd";
var PT_MOUTH = "#7a3330";

/** Segment i of an open Catmull-Rom run [x0, y0, x1, y1, …] as cubic controls [x1, y1, x2, y2, x, y]. */
function ptSeg(p, i) {
  var n = p.length / 2;
  var a = Math.max(0, i - 1), d = Math.min(n - 1, i + 2);
  return [
    p[i * 2] + (p[i * 2 + 2] - p[a * 2]) / 6, p[i * 2 + 1] + (p[i * 2 + 3] - p[a * 2 + 1]) / 6,
    p[i * 2 + 2] - (p[d * 2] - p[i * 2]) / 6, p[i * 2 + 3] - (p[d * 2 + 1] - p[i * 2 + 1]) / 6,
    p[i * 2 + 2], p[i * 2 + 3],
  ];
}

/**
 * A path through smooth runs (r units), each starting where the last one ended, so runs meet at
 * corners (claw tips, scallop points). `sx` −1 mirrors left–right; `close` closes the path.
 */
function ptRuns(runs, r, sx, close) {
  var k = sx || 1;
  return function (c) {
    c.moveTo(runs[0][0] * k * r, runs[0][1] * r);
    for (var j = 0; j < runs.length; j++) {
      var p = runs[j];
      for (var i = 0; i < p.length / 2 - 1; i++) {
        var b = ptSeg(p, i);
        c.bezierCurveTo(b[0] * k * r, b[1] * r, b[2] * k * r, b[3] * r, b[4] * k * r, b[5] * r);
      }
    }
    if (close) c.closePath();
  };
}

/** A part's outline alone, as `part` draws it: `pen` beyond the edge, a touch heavier lower right. */
function ptInk(c, r, path, pen, fill) {
  c.save();
  c.lineJoin = "round";
  c.lineCap = "round";
  c.fillStyle = PT_INK;
  c.strokeStyle = PT_INK;
  c.lineWidth = pen * 2 * r;
  for (var s = 0; s < 2; s++) {
    c.save();
    if (s) c.translate(SWELL[0] * r, SWELL[1] * r);
    c.beginPath();
    path(c);
    if (fill) c.fill();
    c.stroke();
    c.restore();
  }
  c.restore();
}

/**
 * An ink line along segments [from, to) of a run whose half-width follows `stops` [[t, w], …]
 * (t 0 → 1 along its length, w in r units). `swell` adds the outline's lower-right nudge copy;
 * `lean` −1 lays the whole width on the run's left (seen walking along it) instead of centring
 * it, so the line can carry on from an outline drawn beyond a fill edge (no end caps then).
 * `color` paints it in something other than the ink.
 */
function ptTaper(c, r, run, from, to, stops, sx, swell, lean, color) {
  var o = lean || 0;
  var k = sx || 1;
  var pts = [], len = [0];
  for (var i = from; i < to; i++) {
    var b = ptSeg(run, i), x0 = run[i * 2], y0 = run[i * 2 + 1];
    for (var s = i > from ? 1 : 0; s <= 8; s++) {
      var t = s / 8, u = 1 - t;
      pts.push([
        u * u * u * x0 + 3 * u * u * t * b[0] + 3 * u * t * t * b[2] + t * t * t * b[4],
        u * u * u * y0 + 3 * u * u * t * b[1] + 3 * u * t * t * b[3] + t * t * t * b[5],
      ]);
      var m = pts.length - 1;
      if (m) len.push(len[m - 1] + Math.hypot(pts[m][0] - pts[m - 1][0], pts[m][1] - pts[m - 1][1]));
    }
  }
  var n = pts.length, side = [], wid = [];
  for (var j = 0; j < n; j++) {
    var p0 = pts[Math.max(0, j - 1)], p1 = pts[Math.min(n - 1, j + 1)];
    var dx = p1[0] - p0[0], dy = p1[1] - p0[1], d = Math.hypot(dx, dy) || 1;
    var at = len[j] / (len[n - 1] || 1), w = stops[stops.length - 1][1];
    for (var q = 1; q < stops.length; q++) {
      if (at <= stops[q][0]) {
        var f = (at - stops[q - 1][0]) / ((stops[q][0] - stops[q - 1][0]) || 1);
        w = stops[q - 1][1] + (stops[q][1] - stops[q - 1][1]) * Math.max(0, f);
        break;
      }
    }
    wid.push(w);
    side.push([-dy / d * w, dx / d * w]);
  }
  c.save();
  c.fillStyle = color || PT_INK;
  for (var copy = 0; copy < (swell ? 2 : 1); copy++) {
    if (copy) c.translate(SWELL[0] * r, SWELL[1] * r);
    c.beginPath();
    for (j = 0; j < n; j++) {
      c.lineTo((pts[j][0] + side[j][0] * (1 + o)) * k * r, (pts[j][1] + side[j][1] * (1 + o)) * r);
    }
    for (j = n - 1; j >= 0; j--) {
      c.lineTo((pts[j][0] + side[j][0] * (o - 1)) * k * r, (pts[j][1] + side[j][1] * (o - 1)) * r);
    }
    c.closePath();
    c.fill();
    [0, n - 1].forEach(function (e) {
      if (wid[e] <= 0 || o) return;
      c.beginPath();
      c.arc(pts[e][0] * k * r, pts[e][1] * r, wid[e] * r, 0, TAU);
      c.fill();
    });
  }
  c.restore();
}

// The near (right) wing, traced from the picture; the far wing mirrors it.
/** Arm: shoulder crease on the chest → over the knuckled wrist → thumb-claw tip. */
var PT_ARM = [
  0.163, 0.056, 0.24, 0.012, 0.31, -0.032, 0.39, -0.1, 0.44, -0.195, 0.48, -0.252,
  0.522, -0.29, 0.562, -0.31, 0.605, -0.316, 0.651, -0.306, 0.683, -0.276,
];
var PT_THUMB = [0.683, -0.276, 0.652, -0.263, 0.622, -0.258];
var PT_LEAD = [0.622, -0.258, 0.708, -0.256, 0.798, -0.228, 0.882, -0.18, 0.946, -0.11, 0.973, -0.052];
var PT_TRAIL = [0.973, -0.052, 0.888, -0.045, 0.807, -0.015, 0.744, 0.045, 0.699, 0.125, 0.672, 0.215, 0.654, 0.287];
var PT_TRAIL2 = [0.654, 0.287, 0.59, 0.253, 0.51, 0.243, 0.425, 0.26, 0.355, 0.295, 0.324, 0.338];
var PT_EDGE = [PT_ARM, PT_THUMB, PT_LEAD, PT_TRAIL, PT_TRAIL2];
/** Root, unlined: the near wing's melts into the flank under the haunch; the far one hides in the body. */
var PT_ROOT_NEAR = [0.324, 0.338, 0.27, 0.372, 0.2, 0.36, 0.16, 0.27, 0.15, 0.16, 0.163, 0.056];
var PT_ROOT_FAR = [0.324, 0.338, 0.26, 0.4, 0.15, 0.3, 0.12, 0.15, 0.163, 0.056];
/** Outer membrane panel: under the leading band, beyond the finger bone (runs past the trailing edge). */
var PT_PANEL_OUT = [
  [0.598, -0.165, 0.61, -0.2, 0.654, -0.208, 0.717, -0.2, 0.823, -0.165, 0.92, -0.093, 0.963, -0.045, 1.05, 0.0],
  [1.05, 0.0, 1.05, 0.5],
  [1.05, 0.5, 0.656, 0.5],
  [0.656, 0.5, 0.656, 0.33, 0.65, 0.25, 0.636, 0.15, 0.62, 0.05, 0.606, -0.05, 0.598, -0.165],
];
/** Inner membrane panel: from a point under the wrist, between the finger bone and the arm. */
var PT_PANEL_IN = [
  [0.54, -0.115, 0.556, -0.05, 0.575, 0.05, 0.6, 0.15, 0.622, 0.25, 0.636, 0.34, 0.64, 0.5],
  [0.64, 0.5, 0.2, 0.5],
  [0.2, 0.5, 0.2, 0.34, 0.21, 0.24, 0.235, 0.16, 0.29, 0.1, 0.37, 0.04, 0.46, -0.03, 0.54, -0.115],
];
/** Skin-pink along the arm, down to the inner panel (runs past the arm line; clipped to the wing). */
var PT_ARM_SKIN = [
  [0.1, 0.0, 0.2, -0.05, 0.3, -0.1, 0.37, -0.16, 0.43, -0.23, 0.48, -0.29],
  [0.48, -0.29, 0.52, -0.2, 0.54, -0.115],
  [0.54, -0.115, 0.46, -0.03, 0.37, 0.04, 0.29, 0.1, 0.235, 0.16, 0.21, 0.24, 0.2, 0.34, 0.2, 0.5],
  [0.2, 0.5, 0.1, 0.5, 0.1, 0.0],
];

/** One wing: side 1 the near wing over the flank, −1 the far wing behind the body. */
function ptWing(c, r, side) {
  var near = side > 0;
  var outline = ptRuns(PT_EDGE.concat([near ? PT_ROOT_NEAR : PT_ROOT_FAR]), r, side, true);
  var big = r * 2;
  if (near) {
    // Ink the free edge only: the arm line's run down the chest is a taper drawn on top, and the
    // last trailing scallop thins to a point where it meets the tail.
    c.save();
    c.beginPath();
    c.rect(-big, -big, big * 2, big * 2);
    c.rect(0.1 * r, -0.13 * r, 0.288 * r, 0.25 * r);
    c.clip("evenodd");
    ptInk(c, r, ptRuns(PT_EDGE.slice(0, 4), r, side, false), PEN.part, false);
    c.restore();
    ptTaper(c, r, PT_TRAIL2, 0, 5, [[0, PEN.part], [0.55, PEN.part], [1, 0.005]], side, true);
  } else {
    ptInk(c, r, outline, PEN.part, true);
  }
  c.save();
  c.beginPath();
  outline(c);
  c.clip();
  c.fillStyle = PT_WING_BAND;
  c.fillRect(-big, -big, big * 2, big * 2);
  c.fillStyle = PT_SKIN.base;
  c.beginPath();
  ptRuns(PT_ARM_SKIN, r, side, true)(c);
  c.fill();
  // The light band also runs down the arm under its line, thinning out toward the shoulder.
  ptTaper(c, r, PT_ARM.slice(6, 18), 0, 5, [[0, 0], [0.45, 0.04], [1, 0.04]], side, false, 0, PT_WING_BAND);
  // The membrane panels, each with a shade crescent along the wing's lower-right edges.
  var S = 0.04 * r;
  [PT_PANEL_OUT, PT_PANEL_IN].forEach(function (panel) {
    c.save();
    c.beginPath();
    ptRuns(panel, r, side, true)(c);
    c.clip();
    c.fillStyle = PT_MEMBRANE.base;
    c.fillRect(-big, -big, big * 2, big * 2);
    c.beginPath();
    c.rect(-big, -big, big * 2, big * 2);
    c.save();
    c.translate(-S * 0.75, -S);
    outline(c);
    c.restore();
    c.fillStyle = PT_MEMBRANE.shade;
    c.fill("evenodd");
    c.restore();
  });
  c.restore();
  if (near) ptTaper(c, r, PT_ARM, 0, 3, [[0, 0], [1, 0.013]], side, false, -1);
}

/** The far foot, peeping out from under the body on the lower left (drawn before the body). */
function ptFarFoot(c, r) {
  var foot = blob([
    [-0.18, 0.42], [-0.25, 0.425], [-0.284, 0.448], [-0.298, 0.478], [-0.292, 0.51], [-0.266, 0.533],
    [-0.225, 0.541], [-0.18, 0.537], [-0.135, 0.522], [-0.11, 0.49],
  ], r);
  part(c, r, foot, PT_SKIN, { pen: PEN.part, lit: 0.015, shade: 0.025 });
  line(c, r, ["M", -0.226, 0.54, "Q", -0.24, 0.512, -0.229, 0.494, "Q", -0.222, 0.486, -0.213, 0.49], PT_INK, 0.017);
}

/** The tail on the lower right: under the near wing and the haunch, its tip curling up. */
var PT_TAIL = [
  [0.22, 0.36, 0.3, 0.3, 0.4, 0.255, 0.47, 0.24, 0.51, 0.256, 0.534, 0.29, 0.538, 0.33, 0.522, 0.375,
    0.49, 0.418, 0.445, 0.452, 0.39, 0.478, 0.33, 0.495, 0.22, 0.45],
  [0.22, 0.45, 0.22, 0.36],
];
/** The curl line from inside the tip back along the tail's top (light above it, the lit upper face). */
var PT_TAIL_CURL = [0.536, 0.322, 0.5, 0.338, 0.43, 0.348, 0.37, 0.35, 0.33, 0.343];

function ptTail(c, r) {
  part(c, r, ptRuns(PT_TAIL, r, 1, true), PT_SKIN, {
    pen: PEN.part, lit: 0.0, shade: 0.035,
    inside: function (cc) {
      cc.fillStyle = PT_SKIN.light;
      cc.beginPath();
      ptRuns([PT_TAIL_CURL, [0.33, 0.343, 0.3, 0.2, 0.6, 0.2, 0.55, 0.322]], r, 1, true)(cc);
      cc.fill();
    },
  });
  ptTaper(c, r, PT_TAIL_CURL, 0, 4, [[0, 0.016], [1, 0.006]]);
}

/** The near haunch with its foot: round, unlined on top where it sits against the wing and tail. */
var PT_LEG = [
  [0.157, 0.455], [0.155, 0.43], [0.168, 0.39], [0.205, 0.353], [0.255, 0.34], [0.305, 0.353],
  [0.342, 0.39], [0.355, 0.44], [0.342, 0.49], [0.305, 0.527], [0.25, 0.548], [0.19, 0.553],
  [0.135, 0.552], [0.1, 0.54], [0.083, 0.513], [0.092, 0.483], [0.122, 0.462],
];

function ptLeg(c, r) {
  var leg = blob(PT_LEG, r);
  // Ink from the haunch's front edge (tapering up into the flank) round the foot and along the
  // bottom to the tail's lower edge; the same curve as the outline, so the fill covers its inner half.
  var edge = [];
  [3, 2, 1, 0, 16, 15, 14, 13, 12, 11, 10, 9, 8, 7].forEach(function (i) {
    edge.push(PT_LEG[i][0], PT_LEG[i][1]);
  });
  ptTaper(c, r, edge, 1, 12, [[0, 0], [0.13, PEN.part], [1, PEN.part]], 1, true);
  part(c, r, leg, PT_SKIN, { noLine: true, lit: 0.025, shade: 0.03 });
  gloss(c, r, 0.215, 0.385, 0.03, 0.019, -0.5, 0.8);
  // The haunch's front edge drawn again over the fill (a line on the body, like the arm crease),
  // then the toe crease.
  ptTaper(c, r, edge, 1, 4, [[0, 0], [1, 0.012]], 1);
  line(c, r, ["M", 0.152, 0.556, "Q", 0.136, 0.532, 0.145, 0.508, "Q", 0.15, 0.5, 0.159, 0.5], PT_INK, 0.017);
}

/** Head and body in one outline, crest tip first; the right side under the near wing is hidden. */
var PT_BODY = [
  "M", 0.358, -0.7,
  "Q", 0.38, -0.63, 0.355, -0.555,
  "Q", 0.335, -0.46, 0.3, -0.405,
  "Q", 0.375, -0.32, 0.372, -0.2,
  "Q", 0.376, -0.12, 0.37, -0.03,
  "Q", 0.33, 0.17, 0.32, 0.32,
  "Q", 0.29, 0.48, 0.1, 0.51,
  "Q", -0.1, 0.53, -0.2, 0.465,
  "Q", -0.3, 0.38, -0.285, 0.2,
  "Q", -0.275, 0.08, -0.26, 0.04,
  "L", -0.33, 0.0,
  "Q", -0.365, -0.12, -0.345, -0.22,
  "Q", -0.33, -0.39, -0.215, -0.48,
  "Q", -0.12, -0.55, 0.0, -0.56,
  "Q", 0.12, -0.575, 0.2, -0.625,
  "Q", 0.29, -0.69, 0.358, -0.7,
  "Z",
];
var PT_BEAK_PATH = [
  "M", -0.352, -0.005,
  "Q", -0.33, -0.085, -0.24, -0.15,
  "Q", -0.16, -0.21, -0.07, -0.216,
  "Q", 0.005, -0.215, 0.017, -0.14,
  "Q", 0.02, -0.06, -0.05, 0.0,
  "Q", -0.13, 0.042, -0.24, 0.042,
  "Q", -0.335, 0.04, -0.352, -0.005,
  "Z",
];

/** The beak's mouth: a smile line at rest; a little open gape for grin / wow / sleepy moods. */
function ptMouth(c, r, kind, beak) {
  if (kind === "grin" || kind === "o") {
    c.save();
    c.beginPath();
    beak(c);
    c.clip();
    var gape = cmds([
      "M", -0.31, -0.01,
      "Q", -0.1, 0.02, 0.03, -0.115,
      "Q", -0.06, kind === "o" ? 0.1 : 0.08, -0.31, -0.01,
      "Z",
    ], r);
    c.beginPath();
    gape(c);
    c.fillStyle = MOUTH_IN;
    c.fill();
    c.save();
    c.clip();
    c.fillStyle = TONGUE;
    c.beginPath();
    c.ellipse(-0.12 * r, 0.05 * r, 0.08 * r, 0.035 * r, -0.25, 0, TAU);
    c.fill();
    c.restore();
    c.restore();
    c.save();
    c.lineJoin = "round";
    c.strokeStyle = PT_MOUTH;
    c.lineWidth = 0.016 * r;
    c.beginPath();
    gape(c);
    c.stroke();
    c.restore();
  } else if (kind === "smirk") {
    line(c, r, ["M", -0.28, 0.0, "Q", -0.1, 0.0, 0.03, -0.13], PT_MOUTH, 0.018);
  } else {
    line(c, r, ["M", -0.31, -0.008, "Q", -0.1, 0.02, 0.03, -0.115], PT_MOUTH, 0.018);
  }
}

export var pterodactyl = {
  disc: { base: "#c04ad0", light: "#f39cf0", shade: "#7f2aa8", ink: "#5a1680" },
  discPattern: function (c, r) {
    // Deep magenta glass, a rosy glow at the heart, star dust and sparkles.
    glow(c, r, 0, 0, 1, [[0, "rgba(226,100,226,0.32)"], [0.7, "rgba(196,64,212,0.45)"], [1, "rgba(136,34,176,0.32)"]]);
    glow(c, r, -0.05, -0.1, 0.75, [[0, "rgba(255,196,250,0.45)"], [1, "rgba(255,196,250,0)"]]);
    starDust(c, r, 81, 90, "#fff0ff", 0.95);
    starSparkles(c, r, [
      [-0.5, -0.66, 0.075], [0.56, -0.56, 0.06], [-0.6, 0.47, 0.06],
      [0.6, 0.6, 0.085], [0.42, -0.84, 0.04], [-0.25, 0.75, 0.045], [0.82, 0.25, 0.045],
    ], "#ffd6ff");
  },
  draw: function (c, r0, pose) {
    var r = r0 * PT_SCALE;
    c.save();
    // Behind the body: the far wing and the far foot.
    ptWing(c, r, -1);
    ptFarFoot(c, r);
    var body = cmds(PT_BODY, r);
    part(c, r, body, PT_SKIN, {
      lit: 0.03, shade: 0.045,
      light: oval(-0.14, -0.38, 0.13, 0.09, -0.5, r),
      inside: function (cc) {
        // The crest's peach inner face and the cream sheen along its front edge.
        cc.fillStyle = PT_CREST;
        cc.beginPath();
        cmds([
          "M", 0.22, -0.52,
          "Q", 0.27, -0.6, 0.335, -0.665,
          "Q", 0.35, -0.6, 0.325, -0.5,
          "Q", 0.305, -0.44, 0.27, -0.415,
          "Q", 0.235, -0.46, 0.22, -0.52,
          "Z",
        ], r)(cc);
        cc.fill();
        cc.fillStyle = PT_CREST_LIGHT;
        cc.beginPath();
        cmds([
          "M", 0.0, -0.54,
          "Q", 0.17, -0.57, 0.33, -0.675,
          "Q", 0.2, -0.6, 0.02, -0.515,
          "Z",
        ], r)(cc);
        cc.fill();
      },
    });
    gloss(c, r, -0.15, -0.42, 0.035, 0.016, -0.5, 0.8);
    gloss(c, r, -0.075, -0.475, 0.014, 0.01, -0.5, 0.75);
    // Belly.
    var belly = blob([[-0.2, 0.11], [-0.04, 0.1], [0.07, 0.17], [0.08, 0.3], [0.02, 0.4], [-0.12, 0.42], [-0.23, 0.36], [-0.25, 0.22]], r);
    part(c, r, belly, PT_BELLY, { noLine: true, lit: 0.02, shade: 0.03 });
    gloss(c, r, -0.17, 0.19, 0.04, 0.026, -0.4, 0.75);
    // The tail, the near wing over the flank, then the near haunch and foot in front.
    ptTail(c, r);
    ptWing(c, r, 1);
    ptLeg(c, r);
    // Beak (down-left) with its nostril and smile line.
    var beak = cmds(PT_BEAK_PATH, r);
    part(c, r, beak, PT_BEAK, { pen: PEN.part, lit: 0.025, shade: 0.03 });
    gloss(c, r, -0.235, -0.11, 0.02, 0.011, -0.6, 0.85);
    c.fillStyle = PT_MOUTH;
    c.beginPath();
    c.ellipse(-0.128 * r, -0.168 * r, 0.016 * r, 0.009 * r, -0.35, 0, TAU);
    c.fill();
    var k = faceKinds(pose);
    ptMouth(c, r, k.mouth, beak);
    kawaiiFace(c, r, {
      eyes: [[-0.225, -0.247, 0.057, 0.06], [0.129, -0.216, 0.062, 0.065]],
      eye: "#4b1a40", hl: [[-0.08, -0.42, 0.3]],
      line: "#4b1a40", lw: 0.02, skin: PT_SKIN.base,
      mouth: [-0.31, -0.008, -0.1, 0.02, 0.03, -0.115], noMouth: true,
      blush: [[-0.282, -0.172, 0.036, 0.024], [0.205, -0.126, 0.05, 0.03]], blushCol: "#ff6aae",
      clip: body,
    }, pose);
    c.restore();
  },
};

// ---------------------------------------------------------------------------------------
// Tier 9 — T-rex (after trex.png): a green bean of a body, big round head on the left, tail
// curving up on the right, orange spikes from crown to tail, cream belly and tail underside,
// tiny arms, a round near thigh on a flat three-toed foot, a short far leg with a two-toed foot,
// darker green spots on the back.

var TR_SCALE = 0.97;
var TR_SKIN = tone("#2d4d14", "#6aa42e", "#96cb40", "#c6e46e");
var TR_BELLY = tone("#2d4d14", "#e5d79c", "#f6efc6", "#fffbe8");
var TR_SPIKE = tone("#8a4609", "#e47a12", "#f7a428", "#ffd062");
/** Warm cream toes with only a faint tan rim: the foot's own ink wraps round them. */
var TR_TOE = tone("#d6be78", "#e3cf86", "#fbf0b2", "#fffbd8");
var TR_SPOT = "#5b9432";

var TR_BODY = [
  [-0.12, -0.665], [0.1, -0.645], [0.26, -0.56], [0.35, -0.42], [0.38, -0.25], [0.39, -0.1],
  [0.44, 0.02], [0.55, 0.055], [0.66, 0.02], [0.75, -0.05], [0.815, -0.085], [0.865, -0.05],
  [0.885, 0.06], [0.876, 0.19], [0.83, 0.31], [0.74, 0.415], [0.62, 0.515], [0.48, 0.58],
  [0.3, 0.618], [0.1, 0.638], [-0.1, 0.633], [-0.22, 0.602], [-0.33, 0.55], [-0.41, 0.47],
  [-0.455, 0.37], [-0.465, 0.2], [-0.5, 0.1],
  [-0.6, 0.03], [-0.67, -0.08], [-0.69, -0.22], [-0.65, -0.4], [-0.53, -0.555], [-0.34, -0.645],
];

/** Back spikes, crown to tail: [base x, base y, tip x, tip y, half-width]. */
var TR_SPIKES = [
  [-0.21, -0.6, -0.235, -0.75, 0.07],
  [0.0, -0.6, 0.0, -0.785, 0.085],
  [0.15, -0.56, 0.24, -0.71, 0.085],
  [0.26, -0.47, 0.39, -0.575, 0.08],
  [0.31, -0.34, 0.47, -0.42, 0.074],
  [0.33, -0.22, 0.495, -0.27, 0.068],
  [0.34, -0.1, 0.495, -0.12, 0.062],
  [0.38, 0.0, 0.51, -0.015, 0.054],
  [0.53, 0.06, 0.58, -0.045, 0.046],
  [0.63, 0.03, 0.67, -0.08, 0.04],
  [0.71, -0.03, 0.745, -0.125, 0.034],
];

var TR_SPOTS = [
  [0.14, -0.43, 0.026, 0.022], [0.22, -0.36, 0.034, 0.028], [0.28, -0.46, 0.02, 0.018],
  [0.31, -0.27, 0.03, 0.026], [0.21, -0.23, 0.02, 0.018], [0.33, -0.13, 0.026, 0.022],
  [0.26, -0.06, 0.032, 0.026], [0.17, 0.0, 0.02, 0.018], [0.37, 0.07, 0.02, 0.016],
  [0.5, 0.15, 0.03, 0.022], [0.6, 0.11, 0.026, 0.02], [0.69, 0.17, 0.03, 0.022],
  [0.78, 0.09, 0.024, 0.02], [0.58, 0.22, 0.02, 0.016], [0.72, 0.03, 0.018, 0.015],
];

function trToes(c, r, list, s) {
  for (var i = 0; i < list.length; i++) {
    var sy = list[i][3] || s * 0.9;
    part(c, r, oval(list[i][0], list[i][1], list[i][2] || s, sy, 0, r), TR_TOE, { pen: 0.008, lit: 0.008, shade: 0.012 });
  }
}

// Legs, traced from trex.png. The near leg is a round thigh ball, bright at the upper left and
// deep green round its lower back, sitting in front of a flat foot: only a sliver of the foot
// shows under the ball, and three big cream toes peek out in front. The thigh grows out of the
// body, so its front ink is a tapered line (hair-thin where it meets the body) down to the
// toes, and its back ink runs on without a break down the heel, under the foot and round the
// toes. The far leg is a short shin with
// an ankle notch and a foot poking forward with two toes; the belly covers its back half.

/** Near thigh ball: centre, radii (r units). */
var TR_THIGH = [0.24, 0.48, 0.215, 0.215];
/**
 * Near foot in front of the thigh's bottom (to the ink's centreline), toe end forward: a round
 * heel under the thigh's back, and a top that rises gently toward the back.
 */
var TR_FOOT = [
  [0.08, 0.598], [0.03, 0.603], [0.005, 0.618], [-0.007, 0.645], [-0.007, 0.677], [0.007, 0.703],
  [0.033, 0.723], [0.1, 0.735], [0.2, 0.738], [0.3, 0.735], [0.36, 0.73], [0.42, 0.716],
  [0.447, 0.682], [0.455, 0.64], [0.448, 0.6], [0.42, 0.578], [0.33, 0.578], [0.2, 0.588],
];
/** Index of the heel's top in TR_FOOT, where the thigh's back ink comes down into it. */
var TR_HEEL_TOP = 14;
/** Near toes [x, y, rx, ry]. */
var TR_NEAR_TOES = [[0.037, 0.668, 0.035, 0.037], [0.115, 0.676, 0.039, 0.041], [0.208, 0.68, 0.04, 0.042]];
/** Far leg (behind the body): shin, ankle notch, foot forward; the heel runs under the belly. */
var TR_FAR_LEG = [
  "M", -0.468, 0.3,
  "C", -0.478, 0.37, -0.484, 0.44, -0.479, 0.488,
  "C", -0.475, 0.515, -0.468, 0.535, -0.455, 0.553,
  "C", -0.485, 0.552, -0.52, 0.562, -0.532, 0.595,
  "C", -0.542, 0.63, -0.53, 0.665, -0.497, 0.674,
  "C", -0.44, 0.69, -0.33, 0.69, -0.25, 0.668,
  "C", -0.19, 0.65, -0.17, 0.6, -0.22, 0.55,
  "C", -0.3, 0.47, -0.38, 0.36, -0.468, 0.3,
  "Z",
];
var TR_FAR_TOES = [[-0.484, 0.613, 0.031, 0.035], [-0.42, 0.62, 0.035, 0.038]];
/** The tail's cream underside (clipped to the body), from the near thigh's back to the tip. */
var TR_UNDER = [
  [0.4, 0.475], [0.57, 0.448], [0.665, 0.41], [0.755, 0.352], [0.818, 0.29], [0.864, 0.225],
  [0.893, 0.16], [0.915, 0.08], [1.0, 0.2], [0.9, 0.5], [0.7, 0.65], [0.4, 0.68],
];
/** The underside sits in the body's shadow: a warmer, deeper cream than the belly. */
var TR_UNDER_TONE = tone("#2d4d14", "#d4b46a", "#e6cf8e", "#f3e4b4");

/** Open Catmull-Rom run through unit points, `n` steps per span. */
function trexRun(pts, n) {
  var out = [];
  for (var i = 0; i < pts.length - 1; i++) {
    var p0 = pts[Math.max(0, i - 1)], p1 = pts[i], p2 = pts[i + 1], p3 = pts[Math.min(pts.length - 1, i + 2)];
    for (var k = 0; k < n; k++) {
      var t = k / n, t2 = t * t, t3 = t2 * t;
      out.push([0, 1].map(function (a) {
        return 0.5 * (2 * p1[a] + (p2[a] - p0[a]) * t + (2 * p0[a] - 5 * p1[a] + 4 * p2[a] - p3[a]) * t2 +
          (3 * p1[a] - p0[a] - 3 * p2[a] + p3[a]) * t3);
      }));
    }
  }
  out.push(pts[pts.length - 1]);
  return out;
}

/**
 * A hand-inked line along `runs` (smooth runs of unit points, joined at sharp corners), drawn in
 * short round-capped steps whose width swells from a hair at the start (over `head` r units) to
 * `w`, and back to a hair over the last `tail` r units.
 */
function trexInk(c, r, runs, w, head, tail, color) {
  var pts = [];
  for (var i = 0; i < runs.length; i++) {
    var s = trexRun(runs[i], 8);
    pts = pts.concat(i ? s.slice(1) : s);
  }
  var L = [0];
  for (i = 1; i < pts.length; i++) L.push(L[i - 1] + Math.hypot(pts[i][0] - pts[i - 1][0], pts[i][1] - pts[i - 1][1]));
  var total = L[L.length - 1];
  c.save();
  c.strokeStyle = color;
  c.lineCap = "round";
  for (i = 1; i < pts.length; i++) {
    var d = (L[i - 1] + L[i]) / 2;
    var k = Math.min(1, head ? d / head : 1, tail ? (total - d) / tail : 1);
    c.lineWidth = w * (0.15 + 0.85 * Math.sin((k * Math.PI) / 2)) * r;
    c.beginPath();
    c.moveTo(pts[i - 1][0] * r, pts[i - 1][1] * r);
    c.lineTo(pts[i][0] * r, pts[i][1] * r);
    c.stroke();
  }
  c.restore();
}

/** Unit points round the ellipse e = [cx, cy, rx, ry] from angle a0 to a1, `n` steps. */
function trexArc(e, a0, a1, n) {
  var out = [];
  for (var i = 0; i <= n; i++) {
    var a = a0 + ((a1 - a0) * i) / n;
    out.push([e[0] + e[2] * Math.cos(a), e[1] + e[3] * Math.sin(a)]);
  }
  return out;
}

/**
 * The near leg: a soft shadow it casts on the body (`body` clips it), the thigh ball and its
 * ink, the foot in front with its ink, then the toes.
 */
function trexNearLeg(c, r, body) {
  var e = TR_THIGH;
  c.save();
  c.beginPath();
  body(c);
  c.clip();
  [[0.03, 0.5, 0.06, 0.15], [0.45, 0.53, 0.05, 0.12]].forEach(function (s) {
    // Soft tall spots either side of the thigh, where the body curves in behind it.
    c.save();
    c.translate(s[0] * r, s[1] * r);
    c.scale(s[2] / s[3], 1);
    var sg = c.createRadialGradient(0, 0, 0, 0, 0, s[3] * r);
    sg.addColorStop(0, rgba(TR_SKIN.ink, 0.3));
    sg.addColorStop(1, rgba(TR_SKIN.ink, 0));
    c.fillStyle = sg;
    c.fillRect(-2 * r, -r, 4 * r, 2 * r);
    c.restore();
  });
  c.restore();
  // The foot behind the thigh, in its shade: only a sliver shows under the ball.
  part(c, r, blob(TR_FOOT, r), TR_SKIN, {
    noLine: true, lit: 0.01, shade: 0.03,
    inside: function (cc) {
      cc.fillStyle = rgba("#5e8a22", 0.55);
      cc.fillRect(-r, -r, 2 * r, 2 * r);
    },
  });
  part(c, r, oval(e[0], e[1], e[2], e[3], 0, r), TR_SKIN, {
    noLine: true, lit: 0, shade: 0,
    inside: function (cc) {
      // The haunch's volume: bright at the upper left, deep green round the lower back, in one
      // smooth diagonal sweep (the top melts into the body).
      var g = cc.createLinearGradient((e[0] - 0.16) * r, (e[1] - 0.18) * r, (e[0] + 0.12) * r, (e[1] + 0.14) * r);
      g.addColorStop(0, rgba("#c0e248", 0.95));
      g.addColorStop(0.35, rgba("#c0e248", 0));
      g.addColorStop(0.5, rgba("#5e8a22", 0));
      g.addColorStop(0.85, rgba("#5e8a22", 1));
      cc.fillStyle = g;
      cc.fillRect(-r, -r, 2 * r, 2 * r);
    },
  });
  gloss(c, r, 0.16, 0.35, 0.042, 0.026, -0.25, 0.85);
  gloss(c, r, 0.236, 0.344, 0.013, 0.012, 0, 0.4);
  // Ink: down the thigh's front, fading out where the toes come out from under it; and one line
  // from a hair high on its back, down into the heel, under the foot and round the toes.
  trexInk(c, r, [trexArc(e, -2.0, -3.9, 14)], 0.026, 0.13, 0.05, TR_SKIN.ink);
  trToes(c, r, TR_NEAR_TOES);
  var back = trexArc(e, -0.64, 0.43, 10);
  var foot = TR_FOOT.slice(0, TR_HEEL_TOP + 1).reverse();
  foot[foot.length - 1] = [0.066, 0.598];
  trexInk(c, r, [back.concat(foot)], 0.028, 0.11, 0.03, TR_SKIN.ink);
}

export var trex = {
  disc: { base: "#f8a12c", light: "#ffeaa0", shade: "#e8720f", ink: "#b8520a" },
  discPattern: function (c, r) {
    // A sunny glow: pale gold at the heart warming to orange glass at the rim.
    glow(c, r, -0.08, -0.1, 1.0, [
      [0, "rgba(255,240,150,0.95)"], [0.42, "rgba(255,204,80,0.85)"], [0.78, "rgba(250,150,32,0.72)"],
      [1, "rgba(236,112,16,0.6)"],
    ]);
    starDust(c, r, 23, 56, "#fff6d0", 0.8);
  },
  draw: function (c, r0, pose) {
    var r = r0 * TR_SCALE;
    c.save();
    // Spikes first: the body covers their roots.
    for (var i = 0; i < TR_SPIKES.length; i++) {
      var s = TR_SPIKES[i];
      part(c, r, spikePath(s[0], s[1], s[2], s[3], s[4], 0.3, r), TR_SPIKE, { pen: PEN.part, lit: 0.016, shade: 0.016 });
    }
    // Far leg and far arm, behind the body.
    part(c, r, cmds(TR_FAR_LEG, r), TR_SKIN, { pen: PEN.part, lit: 0.018, shade: 0.03 });
    trToes(c, r, TR_FAR_TOES);
    part(c, r, blob([[-0.47, 0.12], [-0.565, 0.18], [-0.597, 0.27], [-0.565, 0.333], [-0.49, 0.343], [-0.45, 0.28]], r), TR_SKIN, {
      pen: PEN.part, lit: 0.02, shade: 0.03,
    });
    trToes(c, r, [[-0.55, 0.287], [-0.493, 0.31]], 0.024);
    // Body: head, back and tail in one outline.
    var body = blob(TR_BODY, r);
    part(c, r, body, TR_SKIN, {
      lit: 0.035, shade: 0.06,
      light: oval(-0.36, -0.47, 0.19, 0.1, -0.35, r),
      inside: function (cc) {
        cc.fillStyle = rgba(TR_SPOT, 0.85);
        for (var j = 0; j < TR_SPOTS.length; j++) {
          var p = TR_SPOTS[j];
          cc.beginPath();
          cc.ellipse(p[0] * r, p[1] * r, p[2] * r, p[3] * r, 0.3, 0, TAU);
          cc.fill();
        }
        // The cream underside of the tail, behind the near thigh.
        part(cc, r, blob(TR_UNDER, r), TR_UNDER_TONE, { noLine: true, lit: 0.014, shade: 0.035 });
        // Belly, with its faint plates; the head's chin line across its top.
        var belly = blob([[-0.52, 0.06], [-0.3, 0.1], [-0.1, 0.125], [0.02, 0.22], [0.05, 0.4], [-0.01, 0.56], [-0.15, 0.68], [-0.38, 0.68], [-0.52, 0.5], [-0.54, 0.25]], r);
        part(cc, r, belly, TR_BELLY, { noLine: true, lit: 0.02, shade: 0.05 });
        cc.save();
        cc.beginPath();
        belly(cc);
        cc.clip();
        cc.strokeStyle = rgba(TR_BELLY.shade, 0.9);
        cc.lineWidth = 0.012 * r;
        cc.lineCap = "round";
        [0.24, 0.34, 0.44, 0.535].forEach(function (y) {
          cc.beginPath();
          cc.moveTo(-0.5 * r, (y - 0.02) * r);
          cc.quadraticCurveTo(-0.22 * r, (y + 0.035) * r, 0.06 * r, (y - 0.02) * r);
          cc.stroke();
        });
        cc.restore();
        line(cc, r, ["M", -0.53, 0.07, "Q", -0.3, 0.13, -0.08, 0.12, "Q", 0.0, 0.13, 0.03, 0.2], rgba(TR_SKIN.shade, 0.9), 0.022);
      },
    });
    gloss(c, r, -0.38, -0.53, 0.075, 0.032, -0.4, 0.85);
    gloss(c, r, -0.5, -0.44, 0.022, 0.016, -0.6, 0.75);
    gloss(c, r, 0.62, -0.02, 0.03, 0.014, -0.6, 0.6);
    // Near leg: a round thigh on a flat foot with three cream toes, grown out of the body.
    trexNearLeg(c, r, body);
    // Near arm, hanging from the shoulder, lined along its free edge; two cream claws.
    part(c, r, blob([[-0.06, 0.16], [-0.15, 0.19], [-0.21, 0.25], [-0.235, 0.315], [-0.19, 0.37], [-0.11, 0.38], [-0.02, 0.345], [0.05, 0.29], [0.06, 0.21]], r), TR_SKIN, {
      noLine: true, lit: 0.022, shade: 0.03,
    });
    trexInk(c, r, [[[-0.05, 0.158], [-0.14, 0.188], [-0.205, 0.238], [-0.232, 0.3], [-0.217, 0.357], [-0.15, 0.379],
      [-0.07, 0.367], [0.0, 0.333], [0.05, 0.292]]], 0.026, 0.12, 0.1, TR_SKIN.ink);
    gloss(c, r, -0.104, 0.239, 0.016, 0.013, 0, 0.8);
    trToes(c, r, [[-0.187, 0.315], [-0.117, 0.333]], 0.027);
    kawaiiFace(c, r, {
      eyes: [[-0.51, -0.32, 0.075, 0.08], [-0.06, -0.27, 0.085, 0.09]],
      eye: "#16140f", hl: [[0.1, -0.4, 0.34]],
      line: "#1d2410", lw: 0.024, skin: TR_SKIN.base,
      mouth: [-0.405, -0.215, -0.305, -0.13, -0.205, -0.21],
      blush: [[-0.62, -0.2, 0.06, 0.045], [0.06, -0.165, 0.075, 0.05]], blushCol: "#f2603e",
      clip: body,
    }, pose);
    c.restore();
  },
};

// ---------------------------------------------------------------------------------------
// Tier 10 — Dragon (after dragon_final.png): a big round turquoise head, ringed gold horns (the
// left one behind the head, the right one sitting on it), faceted gold scales on the crown and
// down the neck, bat wings (teal bones, scalloped membranes going from violet at the bones to
// pink and peach at the edge), a cream belly plate, chunky legs with big gold claws, and the
// tail curling up on the right. Its bubble is plain violet-rimmed glass round a warm rainbow
// glow, like the original's.

var DR_SCALE = 0.96;
var DR_INK = "#0b4d72";
var DR_SKIN = tone(DR_INK, "#138cad", "#20c4cc", "#74e7e3");
/** Body, legs and tail: a step deeper than the head, so the big round head leads like the original. */
var DR_BODY = tone(DR_INK, "#0f7aa4", "#19adc2", "#62d9de");
var DR_BELLY = tone("#a1702a", "#e8bd66", "#f6dc8e", "#fff3c4");
/** Gem-cut gold: a bright facet toward the light, an amber one toward the lower right. */
var DR_GOLD = tone("#874308", "#ea8e1c", "#ffc43e", "#ffe68e");
/** Wing membrane: see-through lilac by the bones, warming to pink and then peach at the scalloped edge. */
var DR_WING = tone("#7e3f5c", "#8e62dc", "#b590f2", "#d6bffc");
var DR_WING_PINK = "#e58ad8";
var DR_WING_PEACH = "#ffc2b4";
/** Leg claws: the gold, with a lighter amber-brown rim so they read as slim talons. */
var DR_CLAW = tone("#a85e14", "#ea8e1c", "#ffc43e", "#ffe68e");

/** A smooth open centreline through key points (Catmull-Rom), `n` samples per span. */
function dragonFinalSpline(key, n) {
  var out = [];
  for (var i = 0; i < key.length - 1; i++) {
    var p0 = key[Math.max(0, i - 1)], p1 = key[i], p2 = key[i + 1], p3 = key[Math.min(key.length - 1, i + 2)];
    var seg = bezierPts(
      p1,
      [p1[0] + (p2[0] - p0[0]) / 6, p1[1] + (p2[1] - p0[1]) / 6],
      [p2[0] - (p3[0] - p1[0]) / 6, p2[1] - (p3[1] - p1[1]) / 6],
      p2,
      n
    );
    for (var j = i === 0 ? 0 : 1; j < seg.length; j++) out.push(seg[j]);
  }
  return out;
}

/** A tube round a centreline whose half-width follows wf(t) (t 0 at the root, 1 at the tip). */
function dragonFinalTaperPath(pts, wf, r) {
  return function (c) {
    var n = pts.length, L = [], R = [];
    for (var i = 0; i < n; i++) {
      var a = pts[Math.max(0, i - 1)], b = pts[Math.min(n - 1, i + 1)];
      var dx = b[0] - a[0], dy = b[1] - a[1], d = Math.hypot(dx, dy) || 1;
      var w = wf(i / (n - 1));
      L.push([(pts[i][0] - (dy / d) * w) * r, (pts[i][1] + (dx / d) * w) * r]);
      R.push([(pts[i][0] + (dy / d) * w) * r, (pts[i][1] - (dx / d) * w) * r]);
    }
    c.moveTo(L[0][0], L[0][1]);
    for (i = 1; i < n; i++) c.lineTo(L[i][0], L[i][1]);
    var e = pts[n - 1], f = pts[n - 2];
    var ang = Math.atan2(e[1] - f[1], e[0] - f[0]);
    c.arc(e[0] * r, e[1] * r, wf(1) * r, ang + Math.PI / 2, ang - Math.PI / 2, true);
    for (i = n - 1; i >= 0; i--) c.lineTo(R[i][0], R[i][1]);
    c.closePath();
  };
}

/**
 * A wing bone: teal, thick at the root and thinning toward its tip (a needle point when w1 is
 * 0). `pen` and `t` default to the arm bones' outline and teal.
 */
function dragonFinalBone(c, r, key, w0, w1, k, pen, t) {
  var pts = dragonFinalSpline(key, 5);
  var wf = function (u) {
    return w1 + (w0 - w1) * (1 - Math.pow(u, k));
  };
  part(c, r, dragonFinalTaperPath(pts, wf, r), t || DR_SKIN, { pen: pen != null ? pen : PEN.part, lit: 0.012, shade: 0.012 });
}

/**
 * A wing membrane: `outline` (cmds) closed round the sail, `edge` (cmds) its free scalloped
 * edge, `bones` (cmds) the bones it hangs from. See-through lilac (the glow shows through), a
 * deeper violet hugging the bones, and a broad soft blend to pink and then peach over the
 * lower part, following the scallops, as the light shows through the original's membrane.
 */
function dragonFinalMembrane(c, r, outline, edge, bones) {
  c.save();
  c.globalAlpha = 0.85;
  part(c, r, cmds(outline, r), DR_WING, {
    pen: PEN.fine, lit: 0.025, shade: 0,
    inside: function (cc) {
      line(cc, r, bones, rgba(DR_WING.shade, 0.7), 0.07);
      // Wide, faint strokes stacked narrower toward the edge blend smoothly into it.
      [[0.26, DR_WING_PINK, 0.22], [0.2, DR_WING_PINK, 0.3], [0.14, DR_WING_PINK, 0.4], [0.09, DR_WING_PEACH, 0.5], [0.05, DR_WING_PEACH, 0.7]].forEach(function (b) {
        line(cc, r, edge, rgba(b[1], b[2]), b[0]);
      });
    },
  });
  c.restore();
}

/** A lens pointed at both ends, from (bx, by) to (tx, ty), half-width w. */
function dragonFinalLens(bx, by, tx, ty, w, r) {
  var dx = tx - bx, dy = ty - by, d = Math.hypot(dx, dy) || 1;
  var nx = (-dy / d) * w * 2, ny = (dx / d) * w * 2;
  var mx = (bx + tx) / 2, my = (by + ty) / 2;
  return cmds(["M", bx, by, "Q", mx + nx, my + ny, tx, ty, "Q", mx - nx, my - ny, bx, by, "Z"], r);
}

/**
 * Gem-cut gold (scales, spikes, claws): `path` drawn from base (bx, by) to tip (tx, ty), half-width
 * w. The facet facing the lower right is amber, split from the bright one by a curved ridge
 * (`ridge` bows it toward the shade side, in half-widths), with a white glint on the lit facet.
 */
function dragonFinalGold(c, r, path, bx, by, tx, ty, w, pen, ridge, t) {
  var gold = t || DR_GOLD;
  var rg = ridge != null ? ridge : 0.1;
  var dx = tx - bx, dy = ty - by, d = Math.hypot(dx, dy) || 1;
  var ux = dx / d, uy = dy / d, nx = -uy, ny = ux;
  // The shade facet sits on the side facing the lower right.
  var s = nx + ny * 1.2 >= 0 ? 1 : -1;
  var sx = nx * s, sy = ny * s;
  var mx = (bx + tx) / 2, my = (by + ty) / 2;
  part(c, r, path, gold, {
    pen: pen != null ? pen : PEN.part, lit: Math.min(0.012, w * 0.3), shade: 0,
    inside: function (cc) {
      cc.fillStyle = DR_GOLD.shade;
      cc.beginPath();
      cc.moveTo((bx - ux * w) * r, (by - uy * w) * r);
      cc.quadraticCurveTo((mx + sx * w * rg) * r, (my + sy * w * rg) * r, (tx + ux * 0.01) * r, (ty + uy * 0.01) * r);
      cc.lineTo((tx + sx * w * 3) * r, (ty + sy * w * 3) * r);
      cc.lineTo((bx + sx * w * 3 - ux * w) * r, (by + sy * w * 3 - uy * w) * r);
      cc.closePath();
      cc.fill();
      // The ridge's bright lip, then a glint on the lit facet.
      cc.strokeStyle = rgba(DR_GOLD.light, 0.9);
      cc.lineWidth = Math.max(0.006, w * 0.14) * r;
      cc.lineCap = "round";
      cc.beginPath();
      cc.moveTo((bx + dx * 0.22 - sx * w * 0.12) * r, (by + dy * 0.22 - sy * w * 0.12) * r);
      cc.quadraticCurveTo((mx + sx * w * (rg - 0.06)) * r, (my + sy * w * (rg - 0.06)) * r, (bx + dx * 0.8 - sx * w * 0.1) * r, (by + dy * 0.8 - sy * w * 0.1) * r);
      cc.stroke();
      gloss(cc, r, bx + dx * 0.45 - sx * w * 0.62, by + dy * 0.45 - sy * w * 0.62, Math.max(0.006, d * 0.12), Math.max(0.004, w * 0.16), Math.atan2(dy, dx), 0.85);
    },
  });
}

/** A faceted gold scale or spike, base (s[0], s[1]) → tip (s[2], s[3]), half-width s[4]. */
function drSpike(c, r, s, pen) {
  dragonFinalGold(c, r, spikePath(s[0], s[1], s[2], s[3], s[4], 0.5, r), s[0], s[1], s[2], s[3], s[4], pen);
}

/** A crown scale: a gold lens pointed at both ends. */
function dragonFinalScale(c, r, s) {
  dragonFinalGold(c, r, dragonFinalLens(s[0], s[1], s[2], s[3], s[4], r), s[0], s[1], s[2], s[3], s[4]);
}

/** A gold claw from base (bx, by) to tip (tx, ty): mostly bright, amber down its far side. */
function drClaw(c, r, bx, by, tx, ty, w) {
  dragonFinalGold(c, r, spikePath(bx, by, tx, ty, w, 0.45, r), bx, by, tx, ty, w, PEN.fine, 0.4);
}

/** A slim leg talon pointing nearly straight down, with a lighter rim. */
function drTalon(c, r, bx, by, tx, ty) {
  var w = 0.019;
  dragonFinalGold(c, r, spikePath(bx, by, tx, ty, w, 0.45, r), bx, by, tx, ty, w, PEN.fine, 0.4, DR_CLAW);
}

/** A smooth open line through unit points in the dragon's ink. */
function drInkRun(c, r, key) {
  var pts = dragonFinalSpline(key, 5);
  c.save();
  c.strokeStyle = DR_INK;
  c.lineWidth = PEN.part * 2 * r;
  c.lineCap = "round";
  c.lineJoin = "round";
  c.beginPath();
  c.moveTo(pts[0][0] * r, pts[0][1] * r);
  for (var i = 1; i < pts.length; i++) c.lineTo(pts[i][0] * r, pts[i][1] * r);
  c.stroke();
  c.restore();
}

/**
 * A ringed gold horn from its outer and inner edges (unit points, base → tip, sharing the tip).
 * rings [[ox, oy, ix, iy]] run across it, bowed toward the tip: a deeper band on the base side,
 * a thin line, and a bright lip on the tip side, so each segment reads as a raised ring.
 * `onHead`: the horn sits on the head (drawn over it), so its base gets no outline, only a
 * shadow where it sinks in, like the original's right horn.
 */
function dragonFinalHorn(c, r, outer, inner, rings, onHead) {
  // Each edge is its own smooth curve, so they meet in a sharp point at the tip.
  var a = dragonFinalSpline(outer, 4), b = dragonFinalSpline(inner.slice().reverse(), 4);
  var tip = outer[outer.length - 1];
  var edges = function (cc) {
    cc.moveTo(a[0][0] * r, a[0][1] * r);
    for (var i = 1; i < a.length; i++) cc.lineTo(a[i][0] * r, a[i][1] * r);
    for (var j = 1; j < b.length; j++) cc.lineTo(b[j][0] * r, b[j][1] * r);
  };
  var path = function (cc) {
    edges(cc);
    cc.closePath();
  };
  part(c, r, path, DR_GOLD, {
    pen: PEN.part, lit: 0.04, shade: 0.045, noLine: onHead,
    inside: function (cc) {
      cc.lineCap = "round";
      for (var i = 0; i < rings.length; i++) {
        var g = rings[i];
        var mx = (g[0] + g[2]) / 2, my = (g[1] + g[3]) / 2;
        var tx = tip[0] - mx, ty = tip[1] - my, td = Math.hypot(tx, ty) || 1;
        tx /= td;
        ty /= td;
        var ring = function (off, w, col) {
          cc.strokeStyle = col;
          cc.lineWidth = w * r;
          cc.beginPath();
          cc.moveTo((g[0] + tx * off) * r, (g[1] + ty * off) * r);
          cc.quadraticCurveTo((mx + tx * (off + 0.04)) * r, (my + ty * (off + 0.04)) * r, (g[2] + tx * off) * r, (g[3] + ty * off) * r);
          cc.stroke();
        };
        ring(-0.014, 0.022, rgba(DR_GOLD.shade, 0.45));
        ring(0.011, 0.012, rgba(DR_GOLD.light, 0.9));
        ring(0, 0.008, rgba(DR_GOLD.ink, 0.6));
      }
      if (onHead) {
        // Where the horn sinks into the head: a soft amber shadow along its base.
        cc.strokeStyle = rgba(DR_GOLD.shade, 0.85);
        cc.lineWidth = 0.05 * r;
        cc.beginPath();
        cc.moveTo(b[b.length - 1][0] * r, b[b.length - 1][1] * r);
        cc.lineTo(a[0][0] * r, a[0][1] * r);
        cc.stroke();
      }
    },
  });
  if (onHead) {
    // The outline along both edges only, kept outside the horn like every other part's.
    c.save();
    c.beginPath();
    c.rect(-r * 2, -r * 2, r * 4, r * 4);
    path(c);
    c.clip("evenodd");
    c.lineJoin = "round";
    c.lineCap = "round";
    c.strokeStyle = DR_GOLD.ink;
    c.lineWidth = PEN.part * 2 * r;
    c.beginPath();
    edges(c);
    c.stroke();
    c.restore();
  }
}

/** The tail's centreline: from under the haunch out right, up, and curling in. */
var DR_TAIL = dragonFinalSpline([[0.26, 0.52], [0.44, 0.6], [0.6, 0.6], [0.73, 0.5], [0.775, 0.37], [0.73, 0.255], [0.64, 0.215], [0.575, 0.25], [0.575, 0.31], [0.625, 0.325]], 4);
var DR_TAIL_W = [0.085, 0.02];

/** Gold spikes along the tail's outer edge at sample indices (pointing outward). */
function drTailSpikes(c, r, idx, inner) {
  var pts = DR_TAIL, n = pts.length;
  for (var k = 0; k < idx.length; k++) {
    var i = idx[k];
    var a = pts[Math.max(0, i - 1)], b = pts[Math.min(n - 1, i + 1)];
    var dx = b[0] - a[0], dy = b[1] - a[1], d = Math.hypot(dx, dy) || 1;
    var nx = -dy / d, ny = dx / d;
    if (inner) {
      nx = -nx;
      ny = -ny;
    }
    var w = DR_TAIL_W[0] + (DR_TAIL_W[1] - DR_TAIL_W[0]) * (i / (n - 1));
    var bx = pts[i][0] + nx * w * 0.55, by = pts[i][1] + ny * w * 0.55;
    var len = 0.055 + w * 0.35;
    drSpike(c, r, [bx, by, bx + nx * len + dx / d * 0.012, by + ny * len + dy / d * 0.012, 0.022 + w * 0.12], PEN.fine);
  }
}

// Wings, traced from the original: [outline, free edge, bones under the sail].
var DR_LWING = [
  ["M", -0.53, -0.255, "Q", -0.75, -0.31, -0.902, -0.12, "Q", -0.718, -0.11, -0.72, 0.178,
    "Q", -0.61, 0.028, -0.461, 0.264, "L", -0.4, 0.22, "L", -0.42, -0.05, "Z"],
  ["M", -0.902, -0.12, "Q", -0.718, -0.11, -0.72, 0.178, "Q", -0.61, 0.028, -0.461, 0.264],
  ["M", -0.53, -0.26, "Q", -0.75, -0.3, -0.902, -0.12, "M", -0.575, -0.19, "Q", -0.71, -0.05, -0.718, 0.17],
];
var DR_RWING = [
  ["M", 0.47, -0.23, "Q", 0.7, -0.3, 0.906, -0.085, "Q", 0.71, -0.078, 0.712, 0.155,
    "Q", 0.608, 0.012, 0.472, 0.198, "Q", 0.384, 0.145, 0.29, 0.2, "L", 0.24, 0.1, "Z"],
  ["M", 0.906, -0.085, "Q", 0.71, -0.078, 0.712, 0.155, "Q", 0.608, 0.012, 0.472, 0.198, "Q", 0.384, 0.145, 0.29, 0.2],
  ["M", 0.5, -0.25, "Q", 0.72, -0.29, 0.906, -0.085, "M", 0.53, -0.205, "Q", 0.7, -0.07, 0.712, 0.15,
    "M", 0.225, 0.09, "Q", 0.44, -0.04, 0.476, -0.23],
];

export var dragon_final = {
  disc: { base: "#f08cb8", light: "#ffe07a", shade: "#9446d6", ink: "#6c3ccc" },
  discPattern: function (c, r) {
    // Rainbow glow like the original's: pale gold at the heart, warm amber well out (so the
    // violet wings stand out against it), pink, then the violet glass at the edge.
    glow(c, r, 0.0, -0.03, 0.95, [
      [0, "rgba(255,248,200,0.98)"], [0.45, "rgba(255,222,120,0.94)"], [0.7, "rgba(255,176,110,0.82)"],
      [0.84, "rgba(250,128,170,0.45)"], [1, "rgba(190,90,230,0)"],
    ]);
    starDust(c, r, 57, 90, "#fffbe6", 0.95);
    starSparkles(c, r, [
      [-0.6, -0.55, 0.07], [0.48, -0.66, 0.06], [-0.72, 0.28, 0.07], [0.6, 0.66, 0.06],
      [-0.42, 0.72, 0.05], [0.1, -0.8, 0.04], [0.8, 0.12, 0.045], [-0.15, 0.8, 0.04],
    ], "#fff1b0");
  },
  draw: function (c, r0, pose) {
    var r = r0 * DR_SCALE;
    c.save();
    // Wings, behind everything: the sails, then the finger bones, then the arms over them.
    dragonFinalMembrane(c, r, DR_LWING[0], DR_LWING[1], DR_LWING[2]);
    dragonFinalMembrane(c, r, DR_RWING[0], DR_RWING[1], DR_RWING[2]);
    // Finger bones: thin deeper-teal lines ending in a point at their scallop.
    dragonFinalBone(c, r, [[-0.575, -0.19], [-0.636, -0.121], [-0.692, -0.03], [-0.713, 0.054], [-0.72, 0.178]], 0.009, 0, 1, PEN.fine, DR_BODY);
    dragonFinalBone(c, r, [[0.53, -0.205], [0.6, -0.146], [0.664, -0.074], [0.704, 0.014], [0.72, 0.094], [0.712, 0.155]], 0.009, 0, 1, PEN.fine, DR_BODY);
    // Top bones: tapering evenly to a needle point at the wing tip.
    dragonFinalBone(c, r, [[-0.53, -0.255], [-0.62, -0.264], [-0.7, -0.268], [-0.78, -0.249], [-0.85, -0.198], [-0.902, -0.12]], 0.028, 0, 1.2);
    dragonFinalBone(c, r, [[0.5, -0.25], [0.6, -0.263], [0.7, -0.258], [0.79, -0.222], [0.86, -0.16], [0.906, -0.085]], 0.034, 0, 1.2);
    dragonFinalBone(c, r, [[0.215, 0.105], [0.32, 0.038], [0.41, -0.048], [0.462, -0.155], [0.478, -0.235]], 0.034, 0.03, 1);
    // The thumb claw at the right wing's elbow.
    drClaw(c, r, 0.515, -0.228, 0.466, -0.302, 0.044);
    // Tail curling up on the right, gold spikes on its outer and inner edges.
    drTailSpikes(c, r, [6, 10, 14, 18, 22, 26], false);
    drTailSpikes(c, r, [4, 8, 12], true);
    part(c, r, tubePath(DR_TAIL, DR_TAIL_W[0], DR_TAIL_W[1], r), DR_BODY, { lit: 0.025, shade: 0.035 });
    // Gold spikes down the right of the neck and back, and the small fins on the left of the head.
    [
      [0.24, -0.235, 0.38, -0.345, 0.05],
      [0.265, -0.05, 0.38, -0.135, 0.044],
      [0.215, 0.1, 0.316, 0.026, 0.038],
      [0.19, 0.2, 0.24, 0.12, 0.03],
      [0.27, 0.28, 0.31, 0.215, 0.032],
      [0.33, 0.33, 0.39, 0.26, 0.032],
      [-0.49, -0.29, -0.545, -0.358, 0.03],
      [-0.52, -0.2, -0.585, -0.27, 0.028],
    ].forEach(function (s) { drSpike(c, r, s); });
    // The left horn and a small crown scale, rooted behind the head.
    dragonFinalHorn(c, r,
      [[-0.43, -0.3], [-0.502, -0.33], [-0.499, -0.39], [-0.49, -0.455], [-0.472, -0.515], [-0.443, -0.567], [-0.403, -0.609], [-0.364, -0.648], [-0.337, -0.69], [-0.31, -0.726]],
      [[-0.36, -0.4], [-0.29, -0.49], [-0.266, -0.553], [-0.257, -0.605], [-0.262, -0.656], [-0.279, -0.703], [-0.31, -0.726]],
      [[-0.497, -0.425, -0.31, -0.465], [-0.474, -0.51, -0.272, -0.535], [-0.43, -0.58, -0.258, -0.598], [-0.37, -0.643, -0.262, -0.655]],
      false);
    dragonFinalScale(c, r, [-0.01, -0.47, 0.086, -0.565, 0.036]);
    // Body, the far front leg, belly plate.
    var torso = blob([[-0.4, 0.06], [-0.2, 0.1], [0.1, 0.08], [0.26, 0.14], [0.36, 0.28], [0.42, 0.42], [0.4, 0.58], [0.3, 0.66], [0.0, 0.69], [-0.3, 0.66], [-0.44, 0.56], [-0.46, 0.38], [-0.44, 0.2]], r);
    part(c, r, torso, DR_BODY, {
      lit: 0.03, shade: 0.05,
      inside: function (cc) {
        cc.fillStyle = rgba(DR_BODY.shade, 0.75);
        [[0.0, 0.24, 0.022], [0.07, 0.2, 0.02], [0.1, 0.28, 0.024], [0.18, 0.22, 0.022], [0.04, 0.33, 0.018], [0.16, 0.31, 0.018], [0.24, 0.26, 0.02]].forEach(function (p) {
          cc.beginPath();
          cc.arc(p[0] * r, p[1] * r, p[2] * r, 0, TAU);
          cc.fill();
        });
      },
    });
    // Legs grow out of the body: lined along their free edges only, like the original.
    // Far front leg, tucked behind the belly: a knee bulging out, then a round paw with toe bumps.
    var farLeg = [[-0.44, 0.33], [-0.505, 0.375], [-0.555, 0.43], [-0.575, 0.49], [-0.567, 0.545], [-0.6, 0.588],
      [-0.618, 0.635], [-0.6, 0.674], [-0.555, 0.688], [-0.51, 0.681], [-0.468, 0.692], [-0.42, 0.683], [-0.36, 0.668]];
    part(c, r, blob(farLeg.concat([[-0.31, 0.64], [-0.31, 0.56], [-0.35, 0.46], [-0.39, 0.38]]), r), DR_BODY, {
      noLine: true, lit: 0.028, shade: 0.04,
    });
    drInkRun(c, r, farLeg);
    gloss(c, r, -0.5, 0.45, 0.034, 0.018, -1.0, 0.7);
    [[-0.45, 0.5, 0.018], [-0.5, 0.56, 0.014], [-0.42, 0.58, 0.012]].forEach(function (p) {
      gloss(c, r, p[0], p[1], p[2], p[2] * 0.85, 0, 0.35);
    });
    drTalon(c, r, -0.56, 0.62, -0.582, 0.688);
    drTalon(c, r, -0.49, 0.63, -0.505, 0.704);
    drTalon(c, r, -0.415, 0.625, -0.426, 0.7);
    var belly = blob([[-0.4, 0.15], [-0.24, 0.13], [-0.08, 0.16], [-0.062, 0.28], [-0.078, 0.4], [-0.1, 0.52], [-0.135, 0.615], [-0.21, 0.64], [-0.3, 0.61], [-0.375, 0.54], [-0.42, 0.44], [-0.43, 0.33], [-0.42, 0.23]], r);
    part(c, r, belly, DR_BELLY, {
      pen: PEN.fine, lit: 0.02, shade: 0.03,
      inside: function (cc) {
        cc.strokeStyle = rgba(DR_BELLY.ink, 0.55);
        cc.lineWidth = 0.011 * r;
        cc.lineCap = "round";
        [0.29, 0.38, 0.47, 0.555].forEach(function (y) {
          cc.beginPath();
          cc.moveTo(-0.46 * r, (y - 0.015) * r);
          cc.quadraticCurveTo(-0.24 * r, (y + 0.035) * r, -0.02 * r, (y - 0.02) * r);
          cc.stroke();
        });
      },
    });
    // Back leg: a round haunch ball with a small foot tucked under its front, the talons
    // under the foot.
    part(c, r, blob([[0.15, 0.64], [0.2, 0.625], [0.29, 0.628], [0.335, 0.66], [0.32, 0.698], [0.25, 0.71], [0.18, 0.705], [0.145, 0.68]], r), DR_BODY, {
      pen: PEN.part, lit: 0.02, shade: 0.03,
    });
    part(c, r, blob([[0.3, 0.356], [0.385, 0.38], [0.44, 0.45], [0.452, 0.52], [0.435, 0.59], [0.39, 0.635], [0.32, 0.655], [0.25, 0.645], [0.19, 0.6], [0.165, 0.53], [0.18, 0.45], [0.23, 0.385]], r), DR_BODY, {
      pen: PEN.part, lit: 0.03, shade: 0.045,
    });
    gloss(c, r, 0.26, 0.425, 0.045, 0.024, -0.35, 0.75);
    [[0.34, 0.44, 0.02], [0.37, 0.5, 0.016], [0.3, 0.5, 0.017], [0.25, 0.54, 0.014], [0.34, 0.56, 0.013]].forEach(function (p) {
      gloss(c, r, p[0], p[1], p[2], p[2] * 0.85, 0, 0.35);
    });
    drTalon(c, r, 0.2, 0.672, 0.192, 0.738);
    drTalon(c, r, 0.27, 0.676, 0.264, 0.742);
    // Near front leg: grows out of the chest, its front bulging a little, down to a rounded
    // foot that flares forward with soft toe bumps between the talons.
    var nearLeg = [[-0.075, 0.39], [-0.12, 0.47], [-0.165, 0.56], [-0.188, 0.645], [-0.19, 0.69], [-0.155, 0.718],
      [-0.115, 0.733], [-0.078, 0.722], [-0.04, 0.737], [0.0, 0.726], [0.04, 0.737], [0.085, 0.722], [0.13, 0.698],
      [0.155, 0.64], [0.16, 0.57], [0.152, 0.48], [0.115, 0.4]];
    part(c, r, blob(nearLeg.concat([[0.06, 0.34], [-0.05, 0.34]]), r), DR_BODY, {
      noLine: true, lit: 0, shade: 0.045,
    });
    drInkRun(c, r, nearLeg);
    gloss(c, r, -0.04, 0.45, 0.04, 0.02, -1.2, 0.7);
    [[0.04, 0.43, 0.02], [0.08, 0.48, 0.016], [0.02, 0.52, 0.018], [0.09, 0.55, 0.013], [-0.03, 0.58, 0.014]].forEach(function (p) {
      gloss(c, r, p[0], p[1], p[2], p[2] * 0.85, 0, 0.35);
    });
    drTalon(c, r, -0.14, 0.67, -0.156, 0.744);
    drTalon(c, r, -0.06, 0.68, -0.068, 0.756);
    drTalon(c, r, 0.02, 0.68, 0.016, 0.756);
    // The head's soft shadow on the chest, then the big round head.
    c.save();
    c.beginPath();
    torso(c);
    c.clip();
    c.translate(-0.11 * r, 0.16 * r);
    c.rotate(0.04);
    c.scale(1, 0.075 / 0.4);
    var hs = c.createRadialGradient(0, 0, 0, 0, 0, 0.4 * r);
    hs.addColorStop(0, "rgba(10,60,110,0.12)");
    hs.addColorStop(0.6, "rgba(10,60,110,0.09)");
    hs.addColorStop(1, "rgba(10,60,110,0)");
    c.fillStyle = hs;
    c.fillRect(-0.4 * r, -0.4 * r, 0.8 * r, 0.8 * r);
    c.restore();
    var head = blob([
      [-0.13, -0.53], [0.11, -0.47], [0.22, -0.37], [0.29, -0.22], [0.285, -0.08], [0.24, 0.03],
      [0.12, 0.11], [-0.13, 0.135], [-0.36, 0.105], [-0.49, 0.03], [-0.53, -0.1], [-0.51, -0.26],
      [-0.45, -0.39], [-0.31, -0.495],
    ], r);
    part(c, r, head, DR_SKIN, {
      lit: 0.035, shade: 0.05,
      light: oval(-0.3, -0.35, 0.16, 0.08, -0.35, r),
      inside: function (cc) {
        // Scale spots over the crown: light on the lit side, deeper on the shaded right.
        cc.fillStyle = rgba(DR_SKIN.light, 0.85);
        [[-0.37, -0.27, 0.022, 0.016], [-0.15, -0.28, 0.02, 0.014], [-0.09, -0.4, 0.028, 0.018], [0.0, -0.36, 0.024, 0.016],
          [-0.03, -0.44, 0.018, 0.012], [0.07, -0.41, 0.016, 0.012], [-0.16, -0.36, 0.016, 0.012], [0.1, -0.33, 0.014, 0.011]].forEach(function (p) {
          cc.beginPath();
          cc.ellipse(p[0] * r, p[1] * r, p[2] * r, p[3] * r, -0.2, 0, TAU);
          cc.fill();
        });
        cc.fillStyle = rgba(DR_SKIN.shade, 0.8);
        [[0.16, -0.28, 0.022], [0.215, -0.2, 0.017], [0.13, -0.2, 0.015], [0.2, -0.33, 0.015], [0.24, -0.27, 0.013]].forEach(function (p) {
          cc.beginPath();
          cc.arc(p[0] * r, p[1] * r, p[2] * r, 0, TAU);
          cc.fill();
        });
        // Gold forehead dots.
        cc.fillStyle = DR_GOLD.base;
        cc.beginPath();
        cc.arc(-0.272 * r, -0.232 * r, 0.017 * r, 0, TAU);
        cc.moveTo(-0.267 * r, -0.145 * r);
        cc.arc(-0.278 * r, -0.145 * r, 0.011 * r, 0, TAU);
        cc.fill();
      },
    });
    gloss(c, r, -0.36, -0.38, 0.06, 0.025, -0.6, 0.8);
    gloss(c, r, -0.44, -0.3, 0.016, 0.012, -0.6, 0.7);
    // The right horn sits on the head; the big crown scale and the forehead scale in front.
    dragonFinalHorn(c, r,
      [[0.2, -0.28], [0.235, -0.33], [0.28, -0.38], [0.325, -0.43], [0.36, -0.48], [0.38, -0.54], [0.385, -0.59], [0.375, -0.64], [0.35, -0.69], [0.314, -0.726]],
      [[0.04, -0.42], [0.1, -0.48], [0.15, -0.52], [0.2, -0.558], [0.24, -0.586], [0.27, -0.617], [0.29, -0.656], [0.3, -0.695], [0.314, -0.726]],
      [[0.262, -0.35, 0.098, -0.482], [0.315, -0.418, 0.162, -0.53], [0.355, -0.478, 0.212, -0.566], [0.38, -0.548, 0.258, -0.6], [0.385, -0.615, 0.285, -0.645]],
      true);
    dragonFinalScale(c, r, [-0.19, -0.452, -0.045, -0.628, 0.058]);
    dragonFinalScale(c, r, [-0.274, -0.268, -0.221, -0.447, 0.06]);
    kawaiiFace(c, r, {
      eyes: [[-0.435, -0.105, 0.07, 0.083], [-0.015, -0.11, 0.095, 0.093]],
      eye: "#141a33", low: "#2e4f8c", hl: [[-0.12, -0.38, 0.42], [0.5, 0.48, 0.14]],
      line: "#10203a", lw: 0.019, skin: DR_SKIN.base,
      mouth: [-0.34, -0.02, -0.26, 0.06, -0.178, -0.02],
      blush: [[-0.505, -0.012, 0.042, 0.03], [0.086, 0.01, 0.058, 0.04]], blushCol: "#ff6f86",
      clip: head,
    }, pose);
    c.restore();
  },
};

/** @type {Record<string, { disc: Object, discPattern?: Function, draw: Function }>} */
export var DINO_CREATURES = { pterodactyl: pterodactyl, trex: trex, dragon_final: dragon_final };
